<template>
  <!-- Page Content -->
  <div class="h-100 d-flex flex-column">
    <!-- Row: title -->
    <div class="row f-row mb-2 markets-chart-title d-flex align-items-center">
      <div class="col col-6" style="font-size: 1rem">
        {{ againstCurrency ? baseStr : "USD" }} vs. Global Currencies
      </div>
      <slot name="spot"></slot>
    </div>
    <!-- First row: controls -->
    <div
      class="row f-row mt-2 justify-content-center container-controls-interval"
    >
      <div
        class="txt-label-primary-2 text-center"
        style="width: 40px!important;"
      >
        <button
          @click="onUserPlay"
          :class="{ onShowMarkets: !play }"
          class="showMarkets btn"
        >
          <i class="fa fa-pause" v-if="play"></i>
          <i class="fa fa-play" v-if="!play"></i>
        </button>
      </div>
      <div class="col col-6 text-left">
        <div
          class="div-mid-align btn-toolbar"
          role="toolbar"
          aria-label="Toolbar with button groups"
        >
          <div class="btn-group btn-group-toggle btn-block" role="group">
            <template v-if="controls === 'large'">
              <button
                v-for="timeframe in intervalTime"
                :key="timeframe"
                type="button"
                class="btn btn-bucket btn-Mkt"
                @click="pickedTime(timeframe)"
                :class="{ active: pickedTimeframe === timeframe }"
              >
                {{ timeframe }}
              </button>
            </template>
          </div>
        </div>
      </div>
    </div>
    <!-- Second row: charts -->

    <div class="row f-row justify-content-center h-100">
      <div class="col col-12 text-center progress-div" v-if="showLoading">
        <QSLoading
          :isLoading="showLoading"
          :size="35"
          :colorTheme="colorTheme"
        />
      </div>
      <div
        class="col col-12 text-center"
        v-else-if="worldFeaturesData.length === 0"
        style="position: absolute; left: 0; top: 50%; z-index: 10000000;"
      >
        No Data Available
      </div>
      <div
        class="col col-3"
        v-show="againstCurrency"
        style="height:calc(100vh - 111px);"
      >
        <div
          class="h-100 d-flex flex-column"
          id="world_relat-div"
          style="width: 100%;"
        >
          <svg id="svg-world_relat"></svg>
        </div>
      </div>

      <div
        class="col "
        v-bind:class="[againstCurrency ? 'col-6' : 'col-9']"
        style="height:calc(100vh - 111px);"
      >
        <div
          class="h-100 d-flex flex-column"
          id="world_chart-div"
          style="width: 100%; height: 100%;"
        >
          <svg id="svg-world_chart"></svg>
        </div>
      </div>
      <div class="col col-3" style="height:calc(100vh - 111px);">
        <div
          class="h-100 d-flex flex-column"
          id="world_rank-div"
          style="width: 100%; height: 100%;"
        >
          <svg id="svg-world_rank"></svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { D3Chart } from "@/_core/charts/D3Chart";
import { WorldChart } from "@/_core/charts/WorldChart";
import {
  timeFormat,
  select,
  selectAll,
  format,
  scaleLinear,
  timeParse,
  json as jsonD3,
} from "d3";
import {
  colorSelection,
  currencyFormat,
  dateFormat,
  formatPercent,
  formatPercentSm,
  tDays,
  textinactive,
} from "@/_core/_helpers/_parametric_tools";
import {
  sortByKey,
  formatDate_YMD,
  lengthByDate,
  dateRangeToString,
} from "@/_core/_helpers/_helpers";
import { gatewayService } from "@/_services/gateway.service";
import QSLoading from "@/components/QSLoading.vue";

export default {
  name: "CurrencyWorld",
  props: {
    againstCurrency: { required: true },
    colorTheme: { required: true, type: Boolean, default: true },
    controls: { required: true },
    currencyDefs: { required: true },
    currencyNumber: { required: true },
    processData: { required: true },
  },
  components: { QSLoading },
  data() {
    return {
      baseStr: "N.A.",
      chartDateRange: timeFormat("%b %d, %Y")(new Date()),
      color: null,
      colorSelection: colorSelection,
      currencies: [],
      currencyFormat: currencyFormat,
      D3Chart: null,
      D3ChartRelat: null,
      dateFormat: dateFormat,
      daysSelection: tDays,
      formatPercent: formatPercent,
      formatPercentSm: formatPercentSm,
      initCurrencyDate: new Date(
        new Date().setFullYear(new Date().getFullYear() - 11),
      ),
      interval: null,
      intervalTime: [
        "1D",
        "2D",
        "3D",
        "4D",
        "1W",
        "2W",
        "1M",
        "2M",
        "3M",
        "6M",
        "9M",
        "1Y",
        "2Y",
        "3Y",
        "4Y",
        "5Y",
        "10Y",
      ],
      keys: {},
      logo: require("@/assets/images/symbol_blue.svg"),
      pickedTimeframe: "3M",
      play: false,
      renderWorld: 0,
      showLoading: false,
      screenWidth: 0,
      svgNode: null,
      svgNodeRelat: null,
      user: [],
      worldChartData: [],
      worldChartDataRaw: [],
      worldCoordinates: {},
      worldDataById: {},
      worldDirect: {},
      worldFeaturesData: {},
      worldIndexById: {},
      worldLastP: {},
      worldOldP: {},
    };
  },
  created() {
    this.WorldChartMarkets = new WorldChart(
      "world_chart-div",
      "svg-world_chart",
      this,
    );
    this.D3Chart = new D3Chart("world_rank-div", "svg-world_rank");
    this.D3ChartRelat = new D3Chart("world_relat-div", "svg-world_relat");
    try {
      this.user = this.$store.state.user.all.items;
    } catch (error) {
      console.log(`Error user ${error}`);
    }
  },
  mounted() {
    this.readWorldChartData();
    this.updateScreenWidth();
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
  },
  watch: {
    colorTheme() {
      this.renderChart();
    },
    currencyDefs() {
      this.renderChart();
    },
    renderWorld() {
      this.renderChart();
    },
    currencyNumber() {
      select("#world_relat-div")
        .select("svg > *")
        .remove();
      this.worldDataParse();
    },
    againstCurrency() {
      select("#world_chart-div")
        .select("svg > *")
        .remove();
      select("#world_rank-div")
        .select("svg > *")
        .remove();
      select("#world_relat-div")
        .select("svg > *")
        .remove();
      setTimeout(() => this.worldDataParse(), 300);
    },
  },
  methods: {
    updateScreenWidth() {
      this.screenWidth = window.innerWidth;
    },
    validateCurrencyDefsName(currencyDeftoValidate) {
      return Object.keys(currencyDeftoValidate).length === 0 &&
        currencyDeftoValidate.constructor === Object
        ? "USDCOP"
        : this.currencyDefs.currency_name;
    },
    renderChart() {
      let _this = this;
      selectAll(".tooltip").remove();
      // === render rates rank
      var keys = Object.keys(this.worldDataById);
      let rets = [];
      // selectAll(".tooltip").remove();
      keys.forEach(function(d) {
        if (isFinite(_this.worldDataById[d])) {
          rets.push({
            currency: _this.worldIndexById[d],
            currency_ret:
              _this.worldIndexById[d] +
              ": " +
              format(_this.currencyFormat)(_this.worldLastP[d]) +
              " (" +
              _this.formatPercent(_this.worldDataById[d]) +
              ")",
            rate: _this.worldLastP[d],
            oldRate: _this.worldOldP[d],
            ret: _this.worldDataById[d],
          });
        }
      });
      var distinctKeys = [];
      var distinct = [];
      for (var i = 0; i < rets.length; i++) {
        if (distinctKeys.indexOf(rets[i].currency) < 0) {
          distinct.push(rets[i]);
          distinctKeys.push(rets[i].currency);
        }
      }

      sortByKey(distinct, "ret");
      let minMaxRef = Math.max(
        -distinct[0].ret,
        distinct[distinct.length - 1].ret,
      );
      distinctKeys = distinct.map((d) => d.currency);
      let minMaxYAxis = [-minMaxRef, minMaxRef];

      // === calculate rates rank
      let relat = [];
      this.currencyDefs.currency_name = this.validateCurrencyDefsName(
        this.currencyDefs,
      );
      let baseIdx = distinctKeys.indexOf(this.currencyDefs.currency_name);
      let baseP =
        distinct[baseIdx].rate **
        _this.worldDirect[_this.currencyDefs.currency_name];
      let baseOldP =
        distinct[baseIdx].oldRate **
        _this.worldDirect[_this.currencyDefs.currency_name];
      this.baseStr =
        _this.worldDirect[_this.currencyDefs.currency_name] === 1
          ? distinctKeys[baseIdx].slice(-3)
          : distinctKeys[baseIdx].slice(0, 3);

      for (i = 0; i < distinctKeys.length; i++) {
        if (distinctKeys[i] !== this.currencyDefs.currency_name) {
          relat.push({
            name:
              _this.baseStr +
              (_this.worldDirect[distinctKeys[i]] === 1
                ? distinctKeys[i].slice(-3)
                : distinctKeys[i].slice(0, 3)),
            currency: distinctKeys[i],
            rate:
              distinct[i].rate ** _this.worldDirect[distinctKeys[i]] / baseP,
            oldRate:
              distinct[i].oldRate ** _this.worldDirect[distinctKeys[i]] /
              baseOldP,
            x:
              distinct[i].rate ** _this.worldDirect[distinctKeys[i]] /
                baseP /
                (distinct[i].oldRate ** _this.worldDirect[distinctKeys[i]] /
                  baseOldP) -
              1,
            name_rel:
              _this.baseStr +
              (_this.worldDirect[distinctKeys[i]] === 1
                ? distinctKeys[i].slice(-3)
                : distinctKeys[i].slice(0, 3)) +
              ": " +
              _this.formatPercent(
                distinct[i].rate ** _this.worldDirect[distinctKeys[i]] /
                  baseP /
                  (distinct[i].oldRate ** _this.worldDirect[distinctKeys[i]] /
                    baseOldP) -
                  1,
              ),
          });
        }
      }

      // === render choropleth
      this.color = this.WorldChartMarkets.initVis(
        this.worldFeaturesData,
        this.worldDataById,
        this.worldIndexById,
        this.worldCoordinates,
        this.colorSelection,
        this.pickedTimeframe,
        this.logo,
      );
      // === charts
      this.svgNode = this.D3Chart.setSVG(
        0.05,
        100000,
        0.8,
        100000,
        0.075,
        100000,
        0.2,
        8000,
        true,
      );
      // === set axis
      this.svgNode = this.D3Chart.setAxis(
        this.svgNode,
        [0, 1, 2, 3],
        minMaxYAxis,
        null,
        60,
        8,
        false,
        null,
        false,
        true,
        true,
        this.formatPercentSm,
      );
      // set the 2nd x range and domain
      this.svgNode.xMax = 10;
      let xInit = 0;
      var chartDiv = document.getElementById("world_rank-div");
      this.svgNode.xRight = scaleLinear()
        .range([xInit, chartDiv.clientWidth * 0.4])
        .domain([0, this.svgNode.xMax])
        .nice();
      this.svgNode.step = this.svgNode.height / (distinct.length - 1);
      this.svgNode = this.D3Chart.orderLines(
        this.svgNode,
        distinct,
        this.color,
        1,
        -1,
        "currency",
        "ret",
      );
      this.svgNode = this.D3Chart.orderButtons(
        this.svgNode,
        distinct,
        this.color,
        -1,
        textinactive(this.colorTheme),
        "currency_ret",
        "ret",
        "currency",
        this.screenWidth > 1100 && this.controls === "large" ? 0.8 : 0.7,
        0.25,
        1,
        "CURRB",
      );
      // === relative rates
      this.svgNodeRelat = this.D3ChartRelat.setSVG(
        0.025,
        100000,
        0.05,
        100000,
        0.075,
        100000,
        0.1,
        8000,
        true,
      );
      // === set axis
      let yData = relat.map((d) => d.name).reverse();
      let yDataRel = relat.map((d) => d.name_rel).reverse();
      minMaxRef = Math.max(relat[0].x, -relat[relat.length - 1].x);
      this.svgNodeRelat = this.D3ChartRelat.setAxisLinBand(
        this.svgNodeRelat,
        yData,
        yDataRel,
        true,
        [-minMaxRef, minMaxRef],
        10,
        true,
        this.formatPercentSm,
        this.controls === "large" ? 0.8 : 0.6,
      );
      // === horizontal bars ===
      this.svgNodeRelat = this.D3Chart.hBars(
        this.svgNodeRelat,
        relat,
        0.9,
        "CURRREL",
        relat.map((d) => _this.color(d.x)),
        relat.map((d) => _this.color(d.x)),
        0.25,
        "x",
        "name",
        true,
        true,
        true,
      );

      // === render choropleth
      this.WorldChartMarkets.tooltip(
        this.formatPercent,
        this.svgNode,
        2,
        textinactive(this.colorTheme),
        this.color,
        this.svgNodeRelat,
        this.logo,
      );
      this.WorldChartMarkets.gradientBar();
    },
    onUserPlay() {
      this.play = !this.play;
      if (this.play) {
        this.interval = setInterval(this.stepLoop, 1000);
      } else {
        clearInterval(this.interval);
      }
    },
    callWidth() {
      this.updateScreenWidth();
      setTimeout(() => {
        this.renderWorld += 1;
      }, 500);
    },
    // ================ User interactions  ==================== ================ ================
    stepLoop() {
      let index = Object.keys(this.daysSelection).indexOf(this.pickedTimeframe);
      if (index === 0) {
        index = Object.keys(this.daysSelection).length - 1;
      } else {
        index -= 1;
      }
      this.pickedTimeframe = Object.keys(this.daysSelection)[index];
      this.worldDataParse(false);
    },
    worldDataParse(extras = false) {
      const selectedD = lengthByDate(
        this.pickedTimeframe,
        this.keys.filter((d) => d != null).map((d) => timeParse("%Y-%m-%d")(d)),
      );
      const keys = this.keys;
      const len = keys.length - 1;
      this.chartDateRange = dateRangeToString(
        timeParse("%Y-%m-%d")(keys[len - selectedD]),
        timeParse("%Y-%m-%d")(keys[len]),
        true,
        " \u2192 ",
      );
      this.worldChartData.forEach(function(d) {
        if (+d.direct_against_dollar === 0) {
          d.value = d[keys[len - selectedD]] / d[keys[len]] - 1;
          // d.value = -d.value;
        } else {
          d.value = d[keys[len]] / d[keys[len - selectedD]] - 1;
        }
      });
      const worldDataById = {};
      const worldOldP = {};
      const worldLastP = {};
      const worldDirect = {};
      if (extras === true) {
        const worldCurrencyById = {};
        this.worldChartData.forEach(function(d) {
          worldCurrencyById[d.iso] = d.currency;
          worldDataById[d.iso] = +d.value;
          worldOldP[d.iso] = +d[keys[len - selectedD]];
          worldLastP[d.iso] = +d[keys[len]];
          worldDirect[d.currency] = +d.direct_against_dollar === 0 ? 1 : -1;
        });
        this.worldFeaturesData.features.forEach(function(d) {
          d.chartData = worldDataById[d.id];
          d.currency = worldCurrencyById[d.id];
        });
        const worldIndexById = {};
        this.worldChartData.forEach(function(d) {
          d.currency === null
            ? (worldIndexById[d.iso] = Number.NaN)
            : (worldIndexById[d.iso] = d.currency);
        });
        const worldLatLonById = {};
        this.worldChartData.forEach(function(d) {
          worldLatLonById[d.iso] = { 0: +d.latitude, 1: +d.longitude };
        });
        this.worldCoordinates = worldLatLonById;
        this.worldIndexById = worldIndexById;
      } else {
        this.worldChartData.forEach(function(d) {
          worldDataById[d.iso] = +d.value;
          worldOldP[d.iso] = +d[keys[len - selectedD]];
          worldLastP[d.iso] = +d[keys[len]];
          worldDirect[d.currency] = +d.direct_against_dollar === 0 ? 1 : -1;
        });
        this.worldFeaturesData.features.forEach(function(d) {
          d.chartData = worldDataById[d.id];
        });
      }

      this.worldDataById = worldDataById;
      this.worldOldP = worldOldP;
      this.worldLastP = worldLastP;
      this.worldDirect = worldDirect;
      this.renderWorldMethod();
    },
    renderWorldMethod() {
      this.renderWorld += 1;
      this.showLoading = false;
    },
    pickedTime(interval) {
      this.pickedTimeframe = interval;
      this.worldDataParse(false);
    },
    // =================== APIs =================== //
    readWorldChartData() {
      this.showLoading = true;
      let vis = this;
      // TODO: verificar endpoint — se asume que countries_prices reusa
      // /currencies/prices (total_currencies_historical); confirmar contra
      // el gateway corriendo (/docs) o el backend Django.
      return gatewayService.currencies
        .prices({
          format: "json",
          init_date: formatDate_YMD(this.initCurrencyDate),
        })
        .then((data) => {
          data = JSON.parse(JSON.stringify(data));
          this.keys = Object.keys(data[0]);
          this.worldChartData = data;
          this.readWorldFeaturesData();
        })
        .catch(function(error) {
          vis.showLoading = false;
          vis.worldFeaturesData = [];
          console.log(error);
        });
    },
    readWorldFeaturesData() {
      const baseUrl = process.env.BASE_URL || "/";
      jsonD3(`${baseUrl}worldFeaturesData.json`)
        .then((data) => {
          this.worldFeaturesData = data;
          this.worldDataParse(true);
        })
        .catch((error) => {
          this.showLoading = false;
          this.worldFeaturesData = [];
          console.error(`Error loading worldFeaturesData.json ${error}`);
        });
    },
  },
};
</script>

<style>
.countries {
  stroke: var(--textinactive);
  stroke-width: 0.5;
}
.countries .hoved {
  stroke-width: 3;
  stroke: var(--textactive);
}

.axis-title {
  fill: var(--textactive);
}
.axis-sub-title {
  fill: var(--textinactive);
}

.hover-line-world {
  stroke: var(--textactive);
  stroke-width: 3px;
}
</style>
